

import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Col,
  Row,
  Label,
} from "reactstrap";
import TextEditor from "components/editor/TextEditor";
import { useFormik } from "formik";
import * as Yup from "yup";
import { httpRequest } from "utils/httpRequest";
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom"

const AddVideo = () => {
  const [videoLink, setVideoLink] = useState("")
  const [buttonLoading, setButtonLoading] = useState(false);
  const [imageLink, setImageLink] = useState(null);
  const [loading, setLoading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});

  const navigate = useNavigate()

  // Validation Schema
  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .required("Video title is required")
      .min(3, "Title must be at least 3 characters"),
    description: Yup.string()
      .required("Description is required")
      .test('not-empty-html', 'Description is required', function (value) {
        if (!value || value.trim() === '') return false;
        // Remove HTML tags and check if there's actual content
        const textOnly = value.replace(/<[^>]*>/g, '').trim();
        return textOnly.length > 0;
      }),
    // video: Yup.mixed()
    //   .required("A video file is required")
    //   .test(
    //     "fileType",
    //     "Only video files are allowed",
    //     (value) => value && ["video/mp4"].includes(value.type)
    //   ),
    video: Yup.string()
      .required("A video file is required")
      .test(
        "fileType",
        "Invalid youtube url",
        (value) => {
          const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
          const match = value?.match(regex);
          return match ? match[1] : null;
        }
      ),
    image: Yup.mixed()
      .required("Cover image is required")
    // .test(
    //   "fileSize",
    //   "Image must be smaller than 5MB",
    //   (value) => value && value.size <= 5000000
    // )
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      video: null,
      image: null,
      imagePreview: null
    },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      formik.setTouched({
        title: true,
        description: true,
        video: true,
        image: true
      });

      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) return false;
      setButtonLoading(true)
      try {
        const response = await httpRequest(
          "video/add",
          "POST",
          {
            title: values.title,
            description: values.description,
            // video: videoLink,
            video: values.video,
            image: imageLink
          },
          {},
          true,
          true
        );
        if (response?.code === 200) {
          console.log("res", response)
          toast.success("Video added successfully")
          formik.resetForm({
            values: {
              title: "",
              description: "",
              video: null,
              image: null,
              imagePreview: null
            }
          })
          setImageLink(null);
          navigate("/admin/video-management")
        }
      } catch (error) {
        console.log(error);

      } finally {
        setButtonLoading(false)
      }
    },
  });

  const extractVideoId = useMemo(() => {
    if (!formik.values.video) return null;
    const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = formik.values.video?.match(regex);
    return match ? match[1] : null;
  }, [formik.values.video]);

  // Memoize the video URL to prevent recreation on every render
  // const videoPreviewUrl = useMemo(() => {
  //   if (formik.values.video) {
  //     return URL.createObjectURL(formik.values.video);
  //   }
  //   return null;
  // }, [formik.values.video]);

  // Clean up the object URL when component unmounts or video changes
  // useEffect(() => {
  //   return () => {
  //     if (videoPreviewUrl) {
  //       URL.revokeObjectURL(videoPreviewUrl);
  //     }
  //   };
  // }, [videoPreviewUrl]);

  const getUploadedVideoLink = async (file, key = "test") => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const handleUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        console.log(`Upload progress for ${key}: ${percentCompleted}%`);
        setUploadProgress(prev => ({ ...prev, [key]: percentCompleted }));
      };

      const response = await httpRequest(
        "admin/UploadMedia",
        "POST",
        formData,
        {},
        true,
        true,
        "json",
        handleUploadProgress
      );
      return response?.filePath;
    } catch (error) {
      console.log("Upload error:", error);
      throw error;
    }
  };

  const handleVideoChange = async (event) => {
    const file = event.currentTarget.files[0];
    const allowedTypes = ["video/mp4"];

    if (!allowedTypes.includes(file?.type)) {
      toast.error("Unsupported video format. Only MP4 allowed.");
      formik.setFieldValue("video", null);
      return;
    }

    const fileSizeInBytes = file.size;
    const maxFileSize = 2 * 1024 * 1024 * 1024;

    if (fileSizeInBytes > maxFileSize) {
      toast.error("File is too large. Maximum allowed is 2GB.")
      formik.setFieldValue("video", null);
      return;
    }

    formik.setFieldValue("video", file);

    try {
      // Set loading to pending
      setLoading(prev => ({ ...prev, video: "pending" }));

      const uploadedVideoLink = await getUploadedVideoLink(file, "video");
      console.log("Video uploaded:", uploadedVideoLink);

      setVideoLink(uploadedVideoLink);

      // Set loading to success
      setLoading(prev => ({ ...prev, video: "success" }));
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Video upload failed. Please try again.");

      // Set loading to error
      setLoading(prev => ({ ...prev, video: "error" }));
    }
  };







  const handleImageChange = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    formik.setFieldError("image", undefined);

    // First update Formik state and show preview
    await formik.setFieldValue("image", file, false);
    const imageUrl = URL.createObjectURL(file);
    formik.setFieldValue("imagePreview", imageUrl, false);

    // Upload the image
    try {
      setLoading(prev => ({ ...prev, image: "pending" }));

      const uploadedImageLink = await getUploadedVideoLink(file, "image"); // You might want to rename this function for clarity
      setImageLink(uploadedImageLink);

      // Validate if form was previously submitted
      if (formik.submitCount > 0) {
        await formik.validateField("image");
      }

      setLoading(prev => ({ ...prev, image: "success" }));
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Image upload failed. Please try again.");

      setLoading(prev => ({ ...prev, image: "error" }));
    }
  };












  return (
    <Row className="mt-4 justify-content-center">
      <Col lg="8">
        <Card className="bg-secondary shadow border-0">
          <CardHeader className="bg-transparent pb-3">
            <div className="text-muted text-center mt-2 mb-3">
              <small>Add New Video</small>
            </div>
          </CardHeader>
          <CardBody className="px-lg-5 py-lg-4">
            <Form onSubmit={formik.handleSubmit}>
              {/* Podcast Title */}
              <FormGroup className="mb-3">
                <Label className="form-control-label">Video Title</Label>
                <InputGroup className="input-group-alternative">
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="ni ni-book-bookmark" />
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    placeholder="Enter Video title"
                    type="text"
                    name="title"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={formik.isSubmitting}
                  />
                </InputGroup>
                {formik.touched.title && formik.errors.title && (
                  <div className="text-danger small mt-1">
                    {formik.errors.title}
                  </div>
                )}
              </FormGroup>

              {/* Description */}
              <FormGroup>
                <Label className="form-control-label">Video Description</Label>
                <TextEditor
                  value={formik.values.description}
                  onChange={(html) => {
                    formik.setFieldValue("description", html);
                    formik.setFieldTouched("description", true);
                  }}
                  onBlur={() => {
                    formik.setFieldTouched("description", true);
                  }}
                  placeholder="Write the video description..."
                  hideImageAndLink={false}
                />
                {formik.touched.description && formik.errors.description && (
                  <div className="text-danger small mt-1">
                    {formik.errors.description}
                  </div>
                )}
              </FormGroup>

              {/* Video File */}
              {/* <FormGroup className="mb-4">
                <Label className="form-control-label"> Video</Label> */}

              {/* Upload button and status */}
              {/* <div className="d-flex align-items-center gap-3 mb-3">
                  {formik.values.video ? (
                    <span className="text-success">
                      <i className="ni ni-check-bold" />
                      {formik.values.video.name || "Video selected"}
                    </span>
                  ) : (
                    ""
                  )}

                  <label className="btn btn-sm btn-primary mb-0 ml-2">
                    <i className="ni ni-cloud-upload-96 mr-1" />
                    {formik.values.video ? "Change Video" : "Upload Video"}
                    <input
                      type="file"
                      accept="video/mp4"
                      onChange={handleVideoChange}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div> */}

              {/* Video preview */}
              {/* {videoPreviewUrl && (
                  <div className="mt-2" >
                    <video
                      controls


                      style={{
                        width: "300px",
                        height: "180px",
                        borderRadius: "6px",
                        objectFit: "cover",
                        display: "block"
                      }}
                      src={videoPreviewUrl}
                    />
                  </div>
                )}

                {formik.touched.video && formik.errors.video && (
                  <div className="text-danger small mt-1">
                    {formik.errors.video}
                  </div>
                )}
              </FormGroup>

              <FormGroup className="mb-4">
                <Label className="form-control-label" htmlFor="imageUpload">
                  Upload Cover Image
                </Label>
                <input
                  type="file"
                  name="image"
                  id="imageUpload"
                  accept="image/*"
                  onChange={handleImageChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.image && formik.errors.image && (
                  <div className="text-danger small mt-1">
                    {formik.errors.image}
                  </div>
                )}
              </FormGroup> */}

              {/* Image Preview */}
              {/* {formik.values.imagePreview && (
                <div className="mt-3">
                  <img
                    src={formik.values.imagePreview}
                    alt="Preview"
                    style={{
                      maxWidth: "25%",
                      height: "auto",
                      borderRadius: "0.25rem"
                    }}
                  />
                </div>
              )} */}

              <div class="container text-center addflexwrap-video">
                <div class="row">
                  <div class="col-12 col-md-6">
                    <FormGroup className="mb-4 addflex-videoimage">
                      <Label className="form-control-label"> Video</Label>
                      <InputGroup className="input-group-alternative">
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="fab fa-youtube" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          placeholder="Enter video url"
                          type="text"
                          name="video"
                          value={formik.values.video}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled={formik.isSubmitting}
                        />
                      </InputGroup>
                      {/* Upload button and status */}
                      {/* <div className="d-flex flex-column gap-2 mb-3 addmrginresponsive-video">
                        <label
                          className={`btn btn-sm btn-primary mb-0 ml-2 addp-addvideo position-relative`}
                          style={{ opacity: loading.video === "pending" ? 0.6 : 1, pointerEvents: loading.video === "pending" ? "none" : "auto" }}
                        >
                          {loading.video !== "pending" && <> <i className="ni ni-cloud-upload-96 mr-1" />
                            {formik.values.video ? "Change Video" : "Upload Video"}</>}
                          <input
                            type="file"
                            accept="video/mp4"
                            onChange={handleVideoChange}
                            style={{ display: 'none' }}
                            disabled={formik.isSubmitting || loading.video === "pending"}
                          />
                          {loading.video === "pending" && (
                            <div className="upload-progress-info mt-1 text-white text-center w-100">
                              <i className="fas fa-spinner fa-spin" /> Uploading: {uploadProgress?.video || 0}%
                            </div>
                          )}
                        </label>

                        {loading.video === "pending" && (
                          <div className="progress-bar-line w-100 mt-1">
                            <div
                              className="progress-bar-fill"
                              style={{ width: `${uploadProgress?.video || 0}%` }}
                            />
                          </div>
                        )}
                      </div> */}


                      {/* Video preview */}
                      {/* {videoPreviewUrl && (
                        <div className="mt-2 w-100" >
                          <video
                            controls


                            style={{
                              width: "100%",
                              height: "180px",
                              borderRadius: "6px",
                              objectFit: "cover",
                              display: "block"
                            }}
                            src={videoPreviewUrl}
                          />
                        </div>
                      )} */}

                      {formik.touched.video && formik.errors.video && (
                        <div className="text-danger small mt-1">
                          {formik.errors.video}
                        </div>
                      )}
                    </FormGroup>

                    {extractVideoId && (
                      <div className="mt-2 w-100" >
                        <iframe
                          borderRadius="6px"
                          width="100%"
                          height="180"
                          src={`https://www.youtube.com/embed/${extractVideoId}`}
                          title="YouTube video player"
                          frameborder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          referrerpolicy="strict-origin-when-cross-origin"
                          allowfullscreen
                        ></iframe>
                      </div>
                    )}
                  </div>
                  <div class="col-12 col-md-6">
                    <FormGroup className="mb-3 addflex-videoimage reducemargin-image">
                      <Label className="form-control-label" htmlFor="imageUpload">
                        Upload Cover Image
                      </Label>
                      <div className="d-flex flex-column gap-2 mb-3 addmrginresponsive-image">
                        <label
                          className={`btn btn-sm btn-primary mb-0 ml-2 addp-addvideo position-relative`}
                          style={{ opacity: loading.image === "pending" ? 0.6 : 1, pointerEvents: loading.image === "pending" ? "none" : "auto" }}
                        >
                          {loading.image !== "pending" && <><i className="ni ni-cloud-upload-96 mr-1" />
                            {formik.values.image ? "Change Image" : "Upload Image"}</>}
                          <input
                            type="file"
                            name="image"
                            id="imageUpload"
                            accept="image/*"
                            onChange={handleImageChange}
                            onBlur={formik.handleBlur}
                            style={{ display: 'none' }}
                            disabled={formik.isSubmitting || loading.image === "pending"}
                          />
                          {loading.image === "pending" && (
                            <div className="upload-progress-info mt-1 text-white text-center w-100">
                              <i className="fas fa-spinner fa-spin" /> Uploading: {uploadProgress?.image || 0}%
                            </div>
                          )}
                        </label>

                        {loading.image === "pending" && (
                          <div className="progress-bar-line w-100 mt-1">
                            <div
                              className="progress-bar-fill"
                              style={{ width: `${uploadProgress?.image || 0}%` }}
                            />
                          </div>
                        )}
                      </div>

                      {formik.touched.image && formik.errors.image && (
                        <div className="text-danger small mt-1">
                          {formik.errors.image}
                        </div>
                      )}
                    </FormGroup>

                    {/* Image Preview */}
                    {formik.values.imagePreview && (
                      <div className="mt-3 addmarginbtm-responsiveimage">
                        <img
                          src={formik.values.imagePreview}
                          alt="Preview"
                          className="addwh-image"
                          style={{
                            maxWidth: "25%",
                            height: "auto",
                            borderRadius: "0.25rem"
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>





              <div className="text-center m-2 d-flex align-items-center justify-content-center">
                <Button
                  disabled={
                    buttonLoading || Object.values(loading).some(value => (value === "pending" || value === "error"))
                  }
                  className="mb-3 d-flex align-items-center justify-content-center"
                  color="primary"
                  type="submit"
                >
                  {buttonLoading ? <span className="button-loader"></span> : "Save Video"}
                </Button>

              </div>
            </Form>
          </CardBody>
        </Card>
      </Col >
    </Row >
  );
};

export default AddVideo;